import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import SubmitDeal from "@/pages/SubmitDeal";
import MyDeals from "@/pages/MyDeals";
import DealDetail from "@/pages/DealDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import Calculator from "@/pages/Calculator";
import CreateContract from "@/pages/CreateContract";
import Kpis from "@/pages/Kpis";
import AdminKpis from "@/pages/AdminKpis";
import AdminStudentProfile from "@/pages/AdminStudentProfile";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Leads from "@/pages/Leads";
import Book from "@/pages/Book";
import Calendar from "@/pages/Calendar";
import Community from "@/pages/Community";
import Support from "@/pages/Support";
import Coach from "@/pages/Coach";
import Resources from "@/pages/Resources";
import AdminResources from "@/pages/AdminResources";
import AdminAiCoach from "@/pages/AdminAiCoach";

import AdminOfficeHours from "@/pages/AdminOfficeHours";
import AdminSupport from "@/pages/AdminSupport";
import AdminEmails from "@/pages/AdminEmails";
import Journey from "@/pages/Journey";
import AdminJourney from "@/pages/AdminJourney";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.is_admin ? "/admin" : "/dashboard"} replace />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect session_id in URL fragment synchronously to handle Google OAuth callback
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/journey" element={<Protected adminOnly><Journey /></Protected>} />
      <Route path="/admin/journey" element={<Protected adminOnly><AdminJourney /></Protected>} />
      <Route path="/submit" element={<Protected><SubmitDeal /></Protected>} />
      <Route path="/calculator" element={<Protected><Calculator /></Protected>} />
      <Route path="/contracts" element={<Protected><CreateContract /></Protected>} />
      <Route path="/kpis" element={<Protected><Kpis /></Protected>} />
      <Route path="/deals" element={<Protected><MyDeals /></Protected>} />
      <Route path="/deals/:id" element={<Protected><DealDetail /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/book" element={<Protected><Book /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/community" element={<Protected><Community /></Protected>} />
      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/coach" element={<Protected><Coach /></Protected>} />
      <Route path="/resources" element={<Protected><Resources /></Protected>} />
      <Route path="/admin/resources" element={<Protected adminOnly><AdminResources /></Protected>} />
      <Route path="/admin/ai-coach" element={<Protected adminOnly><AdminAiCoach /></Protected>} />
      <Route path="/integrations" element={<Navigate to="/settings" replace />} />
      <Route path="/admin/office-hours" element={<Protected><AdminOfficeHours /></Protected>} />
      <Route path="/admin/support" element={<Protected adminOnly><AdminSupport /></Protected>} />
      <Route path="/admin/emails" element={<Protected adminOnly><AdminEmails /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/admin/kpis" element={<Protected adminOnly><AdminKpis /></Protected>} />
      <Route path="/admin/students/:id" element={<Protected adminOnly><AdminStudentProfile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            theme="dark"
            position="bottom-right"
            className="wu-toaster"
            toastOptions={{
              style: { background: "#141414", color: "#f5f5f5", border: "1px solid rgba(255,255,255,0.08)" },
            }}
          />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
